import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  AppBar, 
  Toolbar, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
  TextField
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Notifications, 
  AccountCircle,
  Dashboard as DashboardIcon, 
  Call as CallIcon, 
  PhoneCallback as PhoneCallbackIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  Report as ReportIcon,
  ExitToApp as LogoutIcon,
  PhoneForwarded as OutboundIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Error as ErrorIcon,
  HelpOutline as DefaultIcon,
  Schedule as ScheduledIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  PlayArrow as PlayIcon,
  Article as SummaryIcon,
  TextFields as TranscriptIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Science as TestIcon,
  Link as LinkIcon,
  Feedback as FeedbackIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  Storage as DataIcon,
  BarChart as BarChartIcon,
  RestartAlt as RestartAltIcon,
  CheckCircle,
  Cancel,
  HelpOutline,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authService, callsService, organizationService } from '../utils/api';
import { toast } from 'react-toastify';
import FeedbackList from './FeedbackList';

function CallLogs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { callId } = useParams(); // Get callId from URL params
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);

  // Get user role for conditional rendering
  const userRole = authService.getUserRole();
  const canViewCosts = userRole === 'super_admin' || userRole === 'admin';

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Call logs state
  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogsError, setCallLogsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  // Call details modal state
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Download state
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Refresh state
  const [refreshLoading, setRefreshLoading] = useState(false);

  // Page loading state for direct URL access
  const [pageLoading, setPageLoading] = useState(false);

  // Date filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Initial load effect - runs once on component mount
  useEffect(() => {
    // Skip fetching call logs if this is a direct URL access with org query param
    const searchParams = new URLSearchParams(location.search);
    const orgId = searchParams.get('org');
    
    // Only fetch call logs if we have a selected org OR if no org query param (normal usage)
    if (organizationService.getSelectedOrganization() || !orgId) {
      fetchCallLogs(1);
    }
  }, []);

  // URL parameter effect - handles direct URL access
  useEffect(() => {
    // Only handle callId if it's a direct URL access (component just mounted with callId)
    if (callId && !selectedCallId && !modalOpen) {
      const searchParams = new URLSearchParams(location.search);
      const orgId = searchParams.get('org');
      
      setPageLoading(true);
      setSelectedCallId(callId);
      setModalOpen(true);
      fetchCallDetails(callId, orgId).finally(() => {
        setPageLoading(false);
      });
    }
  }, [callId, selectedCallId, modalOpen, location.search]);

  // Load current organization on component mount
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  const currencySymbol = process.env.REACT_APP_CURRENCY_SYMBOL || '';

  // Helper function to format field names
  const formatFieldName = (fieldName) => {
    // Replace underscores with spaces and convert to sentence case
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Fetch call logs
  const fetchCallLogs = async (page = 1) => {
    try {
      setCallLogsLoading(true);
      const logsData = await callsService.getCallLogs(
        page, 
        pageSize, 
        fromDate || null, 
        toDate || null
      );
      
      setCallLogs(logsData.result || []);
      setTotalPages(logsData.total_pages || 1);
      setTotalRecords(logsData.total_records || 0);
      setCurrentPage(logsData.page || 1);
      setCallLogsError(null);
    } catch (err) {
      console.error('Error fetching call logs:', err);
      setCallLogsError('Failed to load call logs');
    } finally {
      setCallLogsLoading(false);
    }
  };

  // Fetch call details
  const fetchCallDetails = async (callId, organizationId = null) => {
    try {
      setCallDetailsLoading(true);
      const detailsData = await callsService.getCallDetails(callId, organizationId);
      if (detailsData.result && detailsData.result.length > 0) {
        setCallDetails(detailsData.result[0]);
      } else {
        // Call not found
        setCallDetails(null);
        if (window.location.pathname.includes(callId)) {
          // If accessed via direct URL and call not found, show error and redirect
          console.error('Call not found:', callId);
          navigate('/call-logs', { replace: true });
        }
      }
    } catch (err) {
      console.error('Error fetching call details:', err);
      setCallDetails(null);
      if (window.location.pathname.includes(callId)) {
        // If accessed via direct URL and error occurred, redirect
        navigate('/call-logs', { replace: true });
      }
    } finally {
      setCallDetailsLoading(false);
    }
  };

  // Handle call row click
  const handleCallClick = async (callId) => {
    setSelectedCallId(callId);
    setModalOpen(true);
    await fetchCallDetails(callId);
    // Update URL to include call ID and org
    const orgId = currentOrganization?.id;
    navigate(`/call-logs/${callId}${orgId ? `?org=${orgId}` : ''}`, { replace: true });
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    // Delay clearing the content to allow modal close animation to complete
    setTimeout(() => {
      setSelectedCallId(null);
      setCallDetails(null);
    }, 200); // Match typical modal close animation duration
    // Navigate back to call-logs without call ID
    navigate('/call-logs', { replace: true });
  };

  // Copy call link to clipboard
  const handleCopyLink = async (callId) => {
    const orgId = currentOrganization?.id;
    const link = `${window.location.origin}/call-logs/${callId}${orgId ? `?org=${orgId}` : ''}`;
    try {
      await navigator.clipboard.writeText(link);
      console.log('Call link copied to clipboard');
      toast.success('Call link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link to clipboard');
    }
  };

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchCallLogs(page);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      await callsService.downloadCallData();
      // Success feedback is handled within the API function
    } catch (error) {
      console.error('Download failed:', error);
      // Could show error message to user
    } finally {
      setDownloadLoading(false);
    }
  };

  // Handle call again
  const handleCallAgain = async (callId) => {
    try {
      await callsService.callAgain(callId);
      toast.success('Call initiated successfully!');
    } catch (error) {
      console.error('Call again failed:', error);
      toast.error(error.message || 'Failed to initiate call again');
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      setRefreshLoading(true);
      // Call refresh API
      await callsService.refreshCalls();
      // Re-fetch call logs after successful refresh
      await fetchCallLogs(currentPage);
    } catch (error) {
      console.error('Refresh failed:', error);
      // Could show error message to user
    } finally {
      setRefreshLoading(false);
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatDuration = (minutes) => {
    const totalMinutes = Math.floor(minutes);
    const seconds = Math.round((minutes - totalMinutes) * 60);
    return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined || isNaN(Number(cost))) return 'N/A';
    return `${currencySymbol}${Number(cost).toFixed(2)}`;
  };

  const getStatusColor = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return 'success.main';
      }
      return 'error.main';
    }
    if (status === 'scheduled') {
      return '#f3a82b';
    }
    return 'text.secondary';
  };

  const getStatusIcon = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return <SuccessIcon fontSize="small" />;
      }
      return <ErrorIcon fontSize="small" />;
    }
    if (status === 'scheduled') {
      return <ScheduledIcon fontSize="small" />;
    }
    return <DefaultIcon fontSize="small" />;
  };

  // Get success evaluation color
  const getSuccessEvaluationColor = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return 'success.main';
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return 'error.main';
    }
    return 'text.secondary'; // For N/A or other values
  };

  // Get success evaluation text
  const getSuccessEvaluationText = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return 'Interested';
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return 'Not Interested';
    }
    return 'N/A';
  };

  // Get success evaluation icon
  const getSuccessEvaluationIcon = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return <CheckCircle fontSize="small" />;
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return <Cancel fontSize="small" />;
    }
    return <HelpOutline fontSize="small" />;
  };

  const getStatusText = (status, endedReason) => {
    if (status === 'ended') {
      if (endedReason === 'customer-ended-call') return 'Successful';
      if (endedReason === 'assistant-ended-call') return 'Successful';
      if (endedReason === 'silence-timed-out') return 'Successful';
      if (endedReason === 'exceeded-max-duration') return 'Successful';
      if (endedReason === 'customer-did-not-answer') return 'No Answer';
      if (endedReason === 'customer-busy') return 'Busy';
      if (endedReason === 'call-rejected') return 'Rejected';
      return 'Failed';
    }
    if (status === 'scheduled') {
      return 'Scheduled';
    }
    return status || 'Unknown';
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ...(currentOrganization?.analytics_enabled !== false ? [{ text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' }] : []),
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...((canViewCosts || userRole === 'moderator') && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Call Logs';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Call Logs
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton color="inherit" onClick={handleOpenMenu}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Call Logs Content */}
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
          {/* Page loading overlay for direct URL access */}
          {pageLoading && (
            <Box sx={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Loading call details...
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Call Logs
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage all your call logs with detailed information
            </Typography>
          </Box>

          <Card sx={{ boxShadow: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 3, 
                borderRadius: '12px 12px 0 0'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <CallIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      All Call Logs
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`${totalRecords} Total Calls`}
                      variant="outlined"
                      color="primary"
                    />
                    <TextField
                      type="date"
                      label="From Date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 160 }}
                    />
                    <TextField
                      type="date"
                      label="To Date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 160 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => fetchCallLogs(1)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Apply Filter
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                        fetchCallLogs(1);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={refreshLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshLoading || downloadLoading}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {refreshLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                      onClick={handleDownload}
                      disabled={downloadLoading || refreshLoading}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {downloadLoading ? 'Downloading...' : 'Download CSV'}
                    </Button>
                  </Box>
                </Box>
                
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 950 }}>
                    <Box sx={{ display: 'flex', borderBottom: 2, borderColor: 'divider', pb: 2 }}>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 16%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        PHONE NUMBER
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 10%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        DURATION
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 10%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        COST
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 10%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        STATUS
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 12%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        ENDED REASON
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 12%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        SUCCESS EVALUATION
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 18%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        STARTED AT
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        flex: '1 1 12%', 
                        color: 'text.primary', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.875rem'
                      }}>
                        ACTIONS
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ p: 3 }}>
                {callLogsError && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {callLogsError}
                  </Typography>
                )}

                {callLogsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                                  ) : (
                    <>
                                            <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 950 }}>
                          {callLogs.map((call) => (
                          <Box 
                            key={call.id} 
                            sx={{ 
                              display: 'flex', 
                              py: 2, 
                              borderBottom: 1, 
                              borderColor: 'divider',
                              alignItems: 'center',
                              '&:hover': {
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                mx: -1,
                                px: 1
                              }
                            }}
                          >
                            <Typography variant="body2" sx={{ 
                              flex: '1 1 16%',
                              fontSize: '0.8rem',
                              fontWeight: 500
                            }}>
                              {call.phone_number}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              flex: '1 1 10%',
                              fontSize: '0.8rem'
                            }}>
                              {formatDuration(call.minutes)}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              flex: '1 1 10%', 
                              fontSize: '0.8rem'
                            }}>
                              {canViewCosts ? formatCost(call.cost) : 'N/A'}
                            </Typography>
                            <Box sx={{ flex: '1 1 10%' }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                color: getStatusColor(call.status, call.ended_reason)
                              }}>
                                {getStatusIcon(call.status, call.ended_reason)}
                                <Typography variant="caption" sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  color: 'inherit'
                                }}>
                                  {getStatusText(call.status, call.ended_reason)}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              flex: '1 1 12%', 
                              color: 'text.secondary',
                              fontSize: '0.8rem'
                            }}>
                              {call.ended_reason ? call.ended_reason.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                            </Typography>
                            <Box sx={{ flex: '1 1 12%' }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                color: getSuccessEvaluationColor(call.successEvaluation)
                              }}>
                                {getSuccessEvaluationIcon(call.successEvaluation)}
                                <Typography variant="caption" sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  color: 'inherit'
                                }}>
                                  {getSuccessEvaluationText(call.successEvaluation)}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              flex: '1 1 18%', 
                              color: 'text.secondary',
                              fontSize: '0.8rem'
                            }}>
                              {formatDate(call.createdAt)}
                            </Typography>
                            <Box sx={{ flex: '1 1 12%' }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleCopyLink(call.id)}
                                  title="Copy direct link"
                                  sx={{ 
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' }
                                  }}
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title="Call Again" placement="top">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleCallAgain(call.id)}
                                    sx={{ 
                                      color: 'success.main',
                                      '&:hover': { 
                                        backgroundColor: 'success.50',
                                        color: 'success.dark'
                                      }
                                    }}
                                  >
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Analytics" placement="top">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleCallClick(call.id)}
                                    sx={{ 
                                      color: 'primary.main',
                                      '&:hover': { 
                                        backgroundColor: 'primary.50',
                                        color: 'primary.dark'
                                      }
                                    }}
                                  >
                                    <BarChartIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                        </Box>
                      </Box>

                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination 
                            count={totalPages} 
                            page={currentPage} 
                            onChange={handlePageChange}
                            color="primary"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
          </Card>
        </Container>

        {/* Call Details Modal */}
        <Dialog 
          open={modalOpen} 
          onClose={handleModalClose} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <PhoneIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Call Details
                </Typography>
                {selectedCallId && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    ID: {selectedCallId}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0 }}>
            {callDetailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={40} />
              </Box>
            ) : callDetails ? (
              <Box>
                {/* Quick Stats Section */}
                <Box sx={{ bgcolor: 'grey.50', p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                        <PhoneIcon sx={{ color: 'primary.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Phone Number
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {callDetails.phoneNumber}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                        <TimeIcon sx={{ color: 'info.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Duration
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatDuration(callDetails.duration)}
                        </Typography>
                      </Card>
                    </Grid>
                    {canViewCosts && (
                      <Grid item xs={12} sm={3}>
                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                          <MoneyIcon sx={{ color: 'success.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Total Cost
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCost(callDetails.totalCost)}
                        </Typography>
                        </Card>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                        <PlayIcon sx={{ color: 'warning.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Recording
                        </Typography>
                        {callDetails.recordingUrl ? (
                          <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 500 }}>
                            Available
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Not Available
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                {/* Content Sections */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Audio Recording Section */}
                    {callDetails.recordingUrl && (
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <PlayIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Call Recording
                            </Typography>
                          </Box>
                          <audio 
                            controls 
                            style={{ width: '100%' }}
                            preload="metadata"
                          >
                            <source src={callDetails.recordingUrl} type="audio/wav" />
                            Your browser does not support the audio element.
                          </audio>
                        </Card>
                      </Grid>
                    )}

                    {/* Call Summary Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            <SummaryIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Call Summary
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {callDetails.summary || 'No summary available for this call.'}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Structured Data Section */}
                    {callDetails.structuredData && (
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <DataIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Structured Data
                            </Typography>
                          </Box>
                          <TableContainer component={Paper} variant="outlined">
                            <Table sx={{ minWidth: 650 }}>
                              <TableBody>
                                {Object.entries(callDetails.structuredData).map(([key, value]) => (
                                  <TableRow key={key} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                    <TableCell 
                                      component="th" 
                                      scope="row" 
                                      sx={{ 
                                        fontWeight: 600,
                                        minWidth: 200,
                                        borderRight: 1,
                                        borderColor: 'divider'
                                      }}
                                    >
                                      {formatFieldName(key)}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>
                                      {value || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Card>
                      </Grid>
                    )}

                    {/* Assistant Variables Section */}
                    {callDetails.assistantVariables && Object.keys(callDetails.assistantVariables).length > 0 && (
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                              <SettingsIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Assistant Variables
                            </Typography>
                          </Box>
                          <TableContainer component={Paper} variant="outlined">
                            <Table sx={{ minWidth: 650 }}>
                              <TableBody>
                                {Object.entries(callDetails.assistantVariables).map(([key, value]) => (
                                  <TableRow key={key} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                    <TableCell 
                                      component="th" 
                                      scope="row" 
                                      sx={{ 
                                        fontWeight: 600,
                                        minWidth: 200,
                                        borderRight: 1,
                                        borderColor: 'divider'
                                      }}
                                    >
                                      {formatFieldName(key)}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>
                                      {value || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Card>
                      </Grid>
                    )}

                    {/* Transcript Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <TranscriptIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Call Transcript
                            </Typography>
                          </Box>
                          {callDetails.transcript && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CopyIcon />}
                              onClick={() => {
                                navigator.clipboard.writeText(callDetails.transcript);
                                // Could add a toast notification here
                              }}
                            >
                              Copy
                            </Button>
                          )}
                        </Box>
                        
                        {callDetails.transcript ? (
                          <Box sx={{ 
                            maxHeight: 400, 
                            overflow: 'auto', 
                            bgcolor: 'grey.50', 
                            p: 3, 
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}>
                            {callDetails.transcript.split('\n').map((line, index) => {
                              const isAI = line.startsWith('AI:');
                              const isUser = line.startsWith('User:');
                              
                              return (
                                <Box key={index} sx={{ mb: 2 }}>
                                  {isAI && (
                                    <Typography variant="caption" sx={{ 
                                      display: 'block',
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      mb: 0.5
                                    }}>
                                      AI Assistant:
                                    </Typography>
                                  )}
                                  {isUser && (
                                    <Typography variant="caption" sx={{ 
                                      display: 'block',
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      mb: 0.5
                                    }}>
                                      Customer:
                                    </Typography>
                                  )}
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      ml: isAI || isUser ? 1 : 0,
                                      lineHeight: 1.6,
                                      color: 'text.primary'
                                    }}
                                  >
                                    {isAI || isUser ? line.substring(line.indexOf(':') + 1).trim() : line}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            textAlign: 'center', 
                            py: 4, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2 
                          }}>
                            <TranscriptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                            <Typography variant="body1" color="text.secondary">
                              No transcript available for this call
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>

                    {/* Feedback Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <FeedbackList callId={selectedCallId} />
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  No call details available
                </Typography>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Button 
              onClick={() => handleCopyLink(selectedCallId)}
              variant="outlined"
              startIcon={<LinkIcon />}
              sx={{ mr: 'auto' }}
            >
              Copy Link
            </Button>
            <Button 
              onClick={handleModalClose} 
              variant="contained"
              color="primary"
              sx={{ minWidth: 100 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default CallLogs; 